import { toast } from "react-toastify";

import {
  fetchNodeStakingComponentDetails,
  fetchPriceDataFromOracle,
  fetchRugProofComponentDetails,
} from "./fetchers";
import { setAmount, setPercentage } from "Store/Reducers/staking";
import { incrementSuccessTxCount } from "Store/Reducers/session";
import { Percentage, StakingTokens } from "Types/reducers";
import { setTxInProgress } from "Store/Reducers/loadings";
import CachedService from "Classes/cachedService";
import {
  formatTokenAmount,
  priceMsgToMorpherString,
  //  priceMsgToMorpherString
} from "./format";
import { store } from "Store";
import { getRdt } from "subs";
import {
  StakeSuccessToast,
  DistributeSuccessToast,
  TxCanceledToast,
  TxFailedToast,
  TxProgressToast,
  UnStakeSuccessToast,
  LockSuccessToast,
  NFTmintToast,
  AssignRewardSuccessToast,
  ClaimSuccessToast,
  ClaimAndStakeSuccessToast,
  ClaimLSUSuccessToast,
  UnlockLSUSuccessToast,
  AirdropToast,
  WithdrawFundUnitSuccessToast,
} from "Components/toasts";
import axios from "axios";
import { HIT_SERVER_URL } from "Constants/endpoints";
import {
  getAirdropRewardsToFomoDirectlyManifest,
  getAssignNodeStakingRewardsManifest,
  getDepositNodeStakingRewardsManifest,
  getDistributeHitTxManifest,
  getDistributeLockHitTxManifest,
  getFinishUnlockLSUProcessManifest,
  getFundUnitValueManifest,
  getHedgeFundWithdrawManifest,
  getLockTxManifest,
  getMintNodeStakingRewardsNFTbadgeManifest,
  getStakeInNodeValidatorManifest,
  getStakeTxManifest,
  getUnlockEarnedLSUManifest,
  getUnStakeTxManifest,
  getWithdrawNodeStakingRewardAndStakeHITManifest,
  getWithdrawNodeStakingRewardsManifest,
} from "./manifests";
import { ClaimableRewardsInfo, RewardTokenDistribution } from "Types/token";
import { generateRandomNonce } from "@radixdlt/radix-engine-toolkit";

type Props = {
  amount: string;
  txManifest: string;
  tokenSymbol: StakingTokens;
  ToastElement: ({
    amount,
    token,
    txId,
  }: {
    amount: string;
    token: StakingTokens;
    txId: string;
  }) => JSX.Element;
};

export const baseTxSender = async ({ amount, txManifest, ToastElement, tokenSymbol }: Props) => {
  const rdt = getRdt();
  let isSuccess = false;
  try {
    if (rdt) {
      store.dispatch(setTxInProgress(true));
      CachedService.TxProgressToast(<TxProgressToast />);
      const result = await rdt.walletApi.sendTransaction({
        transactionManifest: txManifest,
        version: 1,
      });
      if (result.isErr()) throw result.error;
      toast.dismiss();
      CachedService.successToast(
        <ToastElement
          amount={formatTokenAmount(Number(amount))}
          token={tokenSymbol}
          txId={result.value.transactionIntentHash}
        />
      );
      afterSuccessChore();
      isSuccess = true;
      console.log("sendTransaction Result: ", result);
    }
  } catch (error: any) {
    afterErrorChore(error);
  }
  store.dispatch(setTxInProgress(false));
  return isSuccess;
};

export const stakeHIT = async () => {
  try {
    const {
      app: { walletAddress },
      staking: { amount },
    } = store.getState();

    const isSuccess = await baseTxSender({
      amount,
      txManifest: getStakeTxManifest(walletAddress, amount),
      ToastElement: StakeSuccessToast,
      tokenSymbol: StakingTokens.HIT,
    });
    if (isSuccess) {
      await axios.post(`${HIT_SERVER_URL}/emit-stake-message`, {
        message: `${formatTokenAmount(+amount)} HIT has been staked! 💉`,
      });
    }
  } catch (error) {
    console.log("failed sending telegram message");
  }
};

export const unstakeHIT = async () => {
  const {
    app: { walletAddress },
    staking: { amount },
  } = store.getState();

  await baseTxSender({
    amount,
    txManifest: getUnStakeTxManifest(walletAddress, amount),
    ToastElement: UnStakeSuccessToast,
    tokenSymbol: StakingTokens.StHIT,
  });
};

export const distributeHITRewards = async (amount: string, distributeLockedHits: boolean) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    await baseTxSender({
      amount,
      txManifest: distributeLockedHits
        ? getDistributeLockHitTxManifest(walletAddress, amount)
        : getDistributeHitTxManifest(walletAddress, amount),
      ToastElement: DistributeSuccessToast,
      tokenSymbol: StakingTokens.HIT,
    });

    if (distributeLockedHits) {
      fetchRugProofComponentDetails();
    }
  } catch (error) {
    console.log("Unable to distribute Hits");
  }
};

export const LockHITRewards = async (amount: string) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    await baseTxSender({
      amount,
      txManifest: getLockTxManifest(walletAddress, amount),
      ToastElement: LockSuccessToast,
      tokenSymbol: StakingTokens.HIT,
    });
    fetchRugProofComponentDetails();
  } catch (error) {
    console.log("Unable to lock hits");
  }
};

const afterSuccessChore = () => {
  store.dispatch(incrementSuccessTxCount());
  store.dispatch(setAmount("0"));
  store.dispatch(setPercentage(Percentage._0));
};

const afterErrorChore = (error: any) => {
  toast.dismiss();
  if (error?.error === "rejectedByUser") {
    CachedService.errorToast(<TxCanceledToast />);
  } else {
    CachedService.errorToast(<TxFailedToast />);
  }
  console.log("Unable to send Transaction", error);
};

export const mintNodeStakingRewardsNFTbadge = async () => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    await baseTxSender({
      amount: "1",
      txManifest: getMintNodeStakingRewardsNFTbadgeManifest(walletAddress),
      ToastElement: NFTmintToast,
      tokenSymbol: StakingTokens.HIT,
    });
  } catch (error) {
    console.log("Unable to mint node staking nft badge");
  }
};

export const depositNodeStakingRewards = async (
  amount: string,
  tokenSymbol: StakingTokens,
  tokenAddress: string
) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    await baseTxSender({
      amount,
      txManifest: getDepositNodeStakingRewardsManifest(walletAddress, amount, tokenAddress),
      ToastElement: LockSuccessToast,
      tokenSymbol,
    });
    fetchNodeStakingComponentDetails();
  } catch (error) {
    console.log("Unable to lock", tokenSymbol);
  }
};

export const assignNodeStakingRewards = async (
  amount: string,
  rewardTokenDistributions: RewardTokenDistribution[],
  tokenSymbol: StakingTokens,
  rewardTokenAddress: string
) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    await baseTxSender({
      amount,
      txManifest: getAssignNodeStakingRewardsManifest(
        walletAddress,
        rewardTokenDistributions,
        rewardTokenAddress
      ),
      ToastElement: AssignRewardSuccessToast,
      tokenSymbol,
    });
  } catch (error) {
    console.log("Unable to assign", tokenSymbol, "rewards");
  }
};

export const withdrawNodeStakingRewards = async (userNftBadgeId: number) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    await baseTxSender({
      amount: "",
      txManifest: getWithdrawNodeStakingRewardsManifest(walletAddress, userNftBadgeId),
      ToastElement: ClaimSuccessToast,
      tokenSymbol: StakingTokens.HIT,
    });
  } catch (error) {
    console.log("Unable to withdraw node staking rewards");
  }
};

export const withdrawAndAirdropNodeStakingRewardsInFomo = async (userNftBadgeId: number) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    await baseTxSender({
      amount: "",
      txManifest: getAirdropRewardsToFomoDirectlyManifest(walletAddress, userNftBadgeId),
      ToastElement: AirdropToast,
      tokenSymbol: StakingTokens.HIT,
    });
  } catch (error) {
    console.log("Unable to withdraw node staking rewards");
  }
};

export const withdrawNodeStakingRewardsAndStakeHIT = async (
  userNftBadgeId: number,
  claimableRewards: ClaimableRewardsInfo
) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    const isSuccess = await baseTxSender({
      amount: "",
      txManifest: getWithdrawNodeStakingRewardAndStakeHITManifest(walletAddress, userNftBadgeId),
      ToastElement: ClaimAndStakeSuccessToast,
      tokenSymbol: StakingTokens.HIT,
    });

    if (isSuccess) {
      await axios.post(`${HIT_SERVER_URL}/emit-stake-message`, {
        message: `${formatTokenAmount(
          Number(claimableRewards.HIT as string)
        )} HIT has been staked! 💉`,
      });
    }
  } catch (error) {
    console.log("Unable to withdraw node staking rewards");
  }
};

export const stakeInNodeValidator = async (amount: string) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    await baseTxSender({
      amount,
      txManifest: getStakeInNodeValidatorManifest(walletAddress, amount),
      ToastElement: StakeSuccessToast,
      tokenSymbol: StakingTokens.XRD,
    });
  } catch (error) {
    console.log("Unable to stake in node validator");
  }
};

export const unlockNodeEarnedLSUs = async (amount: string) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    return await baseTxSender({
      amount,
      txManifest: getUnlockEarnedLSUManifest(walletAddress, amount),
      ToastElement: UnlockLSUSuccessToast,
      tokenSymbol: StakingTokens.LSU,
    });
  } catch (error) {
    console.log("Unable to unlock LSU in node validator");
  }
};

export const finishNodeLSUnlockProcess = async (amount: string) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();

    return await baseTxSender({
      amount: amount,
      txManifest: getFinishUnlockLSUProcessManifest(walletAddress),
      ToastElement: ClaimLSUSuccessToast,
      tokenSymbol: StakingTokens.LSU,
    });
  } catch (error) {
    console.log("Unable to finish unlock LSU process in node validator");
  }
};

export const withdrawFromHedgeFund = async (amount: string, wantedCoinAddress?: string) => {
  try {
    const {
      app: { walletAddress },
    } = store.getState();
    store.dispatch(setTxInProgress(true));

    // const priceData = await fetchPriceDataFromOracle();
    // if (!priceData) {
    //   toast.error("Failed to fetch price data from Morpher Oracle");
    //   store.dispatch(setTxInProgress(false));
    //   return false;
    // }
    // const morpherMessage = priceMsgToMorpherString(priceData);

    return await baseTxSender({
      amount: amount,
      txManifest: getHedgeFundWithdrawManifest(
        walletAddress,
        amount,
        // morpherMessage,
        // priceData.signature,
        wantedCoinAddress
      ),
      ToastElement: WithdrawFundUnitSuccessToast,
      tokenSymbol: StakingTokens.FUNDUNIT,
    });
  } catch (error) {
    console.log("Unable to finish Withdraw from hedge fund process in node validator");
    store.dispatch(setTxInProgress(false));
  }
};

export const getFundUnitValue = async () => {
  try {
    const txResult = await simulateTx(getFundUnitValueManifest());
    const receipt = txResult.receipt as {
      output: { programmatic_json: { fields: { kind: string; value: string }[] } }[];
    };
    const net_value = receipt?.output[0]?.programmatic_json?.fields?.[0]?.value;
    const gross_value = receipt?.output[0]?.programmatic_json?.fields?.[1]?.value;
    if (!net_value || !gross_value) {
      return undefined;
    }
    return { net_value, gross_value };
  } catch (error) {
    console.log("Unable to get fund unit value");
    return undefined;
  }
};

export const simulateTx = async (manifest: string) => {
  const latestLedgerState =
    await CachedService.gatewayApi.transaction.innerClient.transactionConstruction();

  const nonce = generateRandomNonce();

  const preview = await CachedService.gatewayApi.transaction.innerClient.transactionPreview({
    transactionPreviewRequest: {
      manifest,
      nonce,
      tip_percentage: 0,
      flags: {
        use_free_credit: true,
        assume_all_signature_proofs: true,
        skip_epoch_check: true,
      },
      start_epoch_inclusive: latestLedgerState.ledger_state.epoch,
      end_epoch_exclusive: latestLedgerState.ledger_state.epoch + 1,
      signer_public_keys: [],
    },
  });
  return preview;
};
